import { queryStr, extractCategoryUuid, haveListingFiltersChanged } from '~/utils/plp';

const SHOPWARE_SHOP_CATEGORY_ID = '0192e206b0c27153b6c3697ec6e21c44';
const TWEAKWISE_SHOP_CATEGORY_ID = 'c4ca4238a0b923820dcc509a6f75849b';
const SHOPWARE_DAMES_HEREN_CATEGORY_ID = '0192fc2664b67d73af1a60c80e600c9c';
const TWEAKWISE_DAMES_HEREN_CATEGORY_ID = 'c81e728d9d4c2f636f067f89cc14862c';

interface UsePlpSearchOptions {
    category?: { id: string; parentId?: string };
    sortBy?: string;
    manufacturer: unknown;
    onSearchComplete?: (elements: any[]) => void;
}

/**
 * Raw Shopware products are ~18KB each, and on a full listing that is most of the page
 * payload. The bulk sits in three branches: the media thumbnail sets, the cover's media
 * metadata, and a complete manufacturer entity repeated on every product. The tile reads
 * only the cover/hover image URLs, the cover alt and the manufacturer name (see
 * ShopwareProduct), and GTM only reads the manufacturer name — so those three are slimmed
 * to what is actually rendered and everything else is passed through untouched. The
 * client's own search swaps these back for the full entities as soon as it settles.
 */
function slimProductForPayload(product: any): any {
    if (!product) return product;

    const { media, cover, manufacturer, ...rest } = product;

    return {
        ...rest,
        cover: cover ? { ...cover, media: { url: cover.media?.url, alt: cover.media?.alt } } : cover,
        media: Array.isArray(media)
            ? media.map((entry: any) => ({ position: entry?.position, media: { url: entry?.media?.url } }))
            : media,
        manufacturer: manufacturer
            ? {
                  id: manufacturer.id,
                  name: manufacturer.name,
                  translated: { name: manufacturer.translated?.name },
              }
            : manufacturer,
    };
}

/** Listing state handed from the SSR render to the client through the Nuxt payload. */
interface PlpSsrHandoff {
    allProducts: any[];
    startPage: number;
    endPage: number;
    stableTotal: number;
    stableTotalPages: number;
    stableFilters: any[];
    stableCurrentFilters: any;
    stableSorts: any[];
    lastSettledFullPath: string;
}

export function usePlpSearch(options: UsePlpSearchOptions) {
    const keepFilters = useKeepFilters();
    const { category, sortBy, manufacturer, onSearchComplete } = options;

    const route = useRoute();
    const router = useRouter();

    const pageDataStore = usePageDataStore();

    const brandCategoryId = computed(() => {
        if (!manufacturer) return undefined;

        const pageData = pageDataStore.data as any;
        const variant = pageData?.variant;
        if (variant === 'sale' || variant === 'nieuw') {
            const tweakwiseId = typeof pageData?.tweakwiseId === 'string' ? pageData.tweakwiseId : '';
            const uuid = extractCategoryUuid(tweakwiseId);

            if (uuid && uuid !== ROOT_CATEGORY_ID) return uuid;

            const types = pageData?.manufacturer?.extensions?.baseExtension?.type;
            const isMenOnly = Array.isArray(types) && types.length === 1 && String(types[0]).toLowerCase() === 'heren';
            const genderIds = isMenOnly ? CATEGORY_IDS.men : CATEGORY_IDS.women;
            return variant === 'sale' ? genderIds.sale : genderIds.new;
        }
        return category?.id || undefined;
    });

    const effectiveCategoryId = computed(() => {
        const isNewArrivalsInRoute =
            route.path.includes('Nieuw-binnen') ||
            (typeof route.name === 'string' && route.name.includes('nieuw-binnen')) ||
            route.path.includes('new-arrivals');

        if ((isNewArrivalsInRoute || (!keepFilters.value && queryStr(route.query.sort) === 'nieuw binnen')) && category?.parentId) {
            return category.parentId;
        }

        const catId = category?.id || route.params.categoryId || route.query.categoryId;
        const finalCategoryId = Array.isArray(catId) ? catId[0] : catId;
        if (finalCategoryId === SHOPWARE_SHOP_CATEGORY_ID) return TWEAKWISE_SHOP_CATEGORY_ID;
        if (finalCategoryId === SHOPWARE_DAMES_HEREN_CATEGORY_ID) return TWEAKWISE_DAMES_HEREN_CATEGORY_ID;
        return finalCategoryId ? String(finalCategoryId) : undefined;
    });

    const isSearchPage = computed(() => !!route.query.tn_q);

    const listingType = computed(() => {
        if (isSearchPage.value) return 'productSearchListing';
        return manufacturer ? 'productSearchListing' : 'categoryListing';
    });

    const searchTerm = computed(() => {
        if (isSearchPage.value) return queryStr(route.query.tn_q);
        return manufacturer;
    });

    const {
        getElements,
        getSortingOrders,
        getCurrentPage,
        getTotal,
        getTotalPagesCount,
        getLimit,
        getAvailableFilters,
        getCurrentFilters,
        setCurrentFilters,
        getCurrentListing,
        setInitialListing,
        loading,
        search,
    } = useListing({
        listingType: listingType.value,
        categoryId: effectiveCategoryId.value,
        defaultSearchCriteria: {
            search: searchTerm.value,
        },
    });

    const limit = ref(48);
    const allProducts = ref<any[]>([]);
    const isLoadingMore = ref(false);
    const isLoadingPrevious = ref(false);
    const isRestoringPage = ref(false);
    const startPage = ref(1);
    const endPage = ref(1);
    const productsContainer = ref<HTMLElement | null>(null);

    let searchGeneration = 0;
    let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    // False until the first search for this mounted PLP has settled. The category tree
    // uses it so the Shopware sibling tree (the untrimmed fallback) never paints before
    // Tweakwise has been consulted — that pre-trim paint was the whole-tree flash on
    // client-side navigation. Mount-scoped on purpose: a category nav remounts the page
    // (definePageMeta key = path) and resets it, while filter/sort changes keep it true.
    const hasSettledOnce = ref(false);
    const stableFilters = ref<any[]>([]);
    const stableCurrentFilters = ref<any>({});
    const stableTotal = ref(0);
    const stableTotalPages = ref(0);
    const stableSorts = ref<any[]>([]);
    const stableTweakwiseFacets = useState<any[]>(`stableTweakwiseFacets-${route.path}`, () => []);
    const stableSearchCategoryTree = useState<any | null>(`stableSearchCategoryTree-${route.path}`, () => null);
    const stableManufacturerCategoryTree = useState<any | null>(
        `stableManufacturerCategoryTree-${route.path}`,
        () => null,
    );
    const lastSettledFullPath = ref('');
    const stableTweakwiseCategoryNames = computed<Set<string> | null>(() => {
        const treeFacet = stableTweakwiseFacets.value.find((f: any) => f.type === 'tree');
        if (!treeFacet?.attributes?.length) return null;
        const names = new Set<string>();
        const collect = (items: any[]) => {
            for (const item of items) {
                if (item.title && typeof item.title === 'string') names.add(item.title.toLowerCase());
                if (Array.isArray(item.children)) collect(item.children);
            }
        };
        // Collect all attribute names + any children (recursively)
        collect(treeFacet.attributes);
        return names.size > 0 ? names : null;
    });
    // Narrowed set: only the selected subtree's children (for Sale/Nieuw special pages)
    const stableTweakwiseSelectedSubtreeNames = computed<Set<string> | null>(() => {
        const treeFacet = stableTweakwiseFacets.value.find((f: any) => f.type === 'tree');
        if (!treeFacet?.attributes?.length) return null;
        const selected = treeFacet.attributes.find((a: any) => a.isSelected && a.children?.length);
        if (!selected) return null;
        const names = new Set<string>();
        const collect = (items: any[]) => {
            for (const item of items) {
                if (item.title && typeof item.title === 'string') names.add(item.title.toLowerCase());
                if (Array.isArray(item.children)) collect(item.children);
            }
        };
        collect(selected.children);
        return names.size > 0 ? names : null;
    });

    const effectiveCurrentSort = computed(() => {
        const urlSort = queryStr(route.query.sort);
        if (urlSort || sortBy) return urlSort || sortBy || '';
        const sorts = stableSorts.value?.length ? stableSorts.value : getSortingOrders?.value;
        if (Array.isArray(sorts) && sorts.length > 0) return sorts[0].key;
        return '';
    });

    const { isRestoringFromCache, restoredState, save: savePlpState } = usePlpPersistence(route);

    // SSR resolves the whole listing, but `useListing` keeps it in plain provide/inject
    // refs, so none of it reaches the client. The client's first render therefore began
    // empty + loading and disagreed with the server markup: Vue hydrated the skeleton
    // branch of BlockProductListingProducts onto the product branch, desynced, and left
    // the server-rendered tiles detached from the document — products visible on first
    // paint, then gone for good. Handing the settled state over through the payload makes
    // the first client render identical to the SSR output. One-shot: cleared right after
    // hydration so client-side navigation keeps using the restore/prefetch paths below.
    const ssrHandoff = useState<PlpSsrHandoff | null>(`plp-ssr-${route.fullPath}`, () => null);

    if (import.meta.client && ssrHandoff.value) {
        const handoff = ssrHandoff.value;
        allProducts.value = handoff.allProducts;
        startPage.value = handoff.startPage;
        endPage.value = handoff.endPage;
        stableTotal.value = handoff.stableTotal;
        stableTotalPages.value = handoff.stableTotalPages;
        stableFilters.value = handoff.stableFilters;
        stableCurrentFilters.value = handoff.stableCurrentFilters;
        stableSorts.value = handoff.stableSorts;
        lastSettledFullPath.value = handoff.lastSettledFullPath;
        hasSettledOnce.value = true;
        ssrHandoff.value = null;
    }

    if (restoredState) {
        allProducts.value = restoredState.allProducts;
        startPage.value = restoredState.startPage;
        endPage.value = restoredState.endPage;
        stableTotal.value = restoredState.stableTotal;
        stableTotalPages.value = restoredState.stableTotalPages;
        stableFilters.value = restoredState.stableFilters;
        stableCurrentFilters.value = restoredState.stableCurrentFilters;
        stableSorts.value = restoredState.stableSorts;
        // A restore only happens when the saved querySnapshot equals the current URL, so the
        // path-keyed category tree belongs to this page. Without this the restore skips the
        // search, lastSettledFullPath stays empty, and everything gated on it (auto-select)
        // is blocked for good on client-side navigation.
        lastSettledFullPath.value = route.fullPath;
        hasSettledOnce.value = true;
    }

    // Check for prefetched product listing (fired on category link click).
    // Skip on manufacturer pages — the prefetch uses product-listing (all brands),
    // but manufacturer pages need a /search request filtered by manufacturer.
    const prefetchedListing =
        import.meta.client && !restoredState && !manufacturer && (!keepFilters.value || Object.keys(route.query).length === 0) && effectiveCategoryId.value
            ? consumePlpPrefetch(effectiveCategoryId.value)
            : undefined;

    if (prefetchedListing) {
        setInitialListing(prefetchedListing);
        allProducts.value = withTweakwiseAttributes([...getElements.value], prefetchedListing);
        stableFilters.value = getAvailableFilters.value;
        stableCurrentFilters.value = getCurrentFilters.value;
        stableTweakwiseFacets.value = (prefetchedListing as any)?.extensions?.tweakwiseFacets?.facets ?? [];
        stableSearchCategoryTree.value = (prefetchedListing as any)?.extensions?.categoryTree ?? null;
        if (getSortingOrders.value?.length) {
            stableSorts.value = getSortingOrders.value;
        }
        const twExt = (prefetchedListing as any)?.extensions?.tweakwise;
        if (twExt?.enabled && twExt.totalItems != null) {
            stableTotal.value = twExt.totalItems;
            stableTotalPages.value = twExt.totalPages ?? Math.ceil(twExt.totalItems / (twExt.pageSize || limit.value));
        } else {
            stableTotal.value = getTotal.value;
            stableTotalPages.value = getTotalPagesCount.value;
        }
        isRestoringFromCache.value = true;
        hasSettledOnce.value = true;
    }

    // Keep stableTweakwiseFacets and searchCategoryTree in sync with listing data
    watch(
        getCurrentListing,
        listing => {
            if (listing) {
                const facets = (listing as any)?.extensions?.tweakwiseFacets?.facets;
                if (facets?.length) {
                    stableTweakwiseFacets.value = facets;
                }
                const categoryTree = (listing as any)?.extensions?.categoryTree;
                if (categoryTree) {
                    stableSearchCategoryTree.value = categoryTree;
                    if (manufacturer) {
                        stableManufacturerCategoryTree.value = categoryTree;
                    }
                }
            }
        },
        { immediate: true },
    );

    function updateStableState() {
        stableFilters.value = getAvailableFilters.value;
        stableCurrentFilters.value = getCurrentFilters.value;
        stableTweakwiseFacets.value = (getCurrentListing.value as any)?.extensions?.tweakwiseFacets?.facets ?? [];
        stableSearchCategoryTree.value = (getCurrentListing.value as any)?.extensions?.categoryTree ?? null;
        if (manufacturer) {
            stableManufacturerCategoryTree.value = (getCurrentListing.value as any)?.extensions?.categoryTree ?? null;
        }
        lastSettledFullPath.value = route.fullPath;
        hasSettledOnce.value = true;
        if (getSortingOrders.value?.length) {
            stableSorts.value = getSortingOrders.value;
        }

        const twExt = (getCurrentListing.value as any)?.extensions?.tweakwise;
        if (twExt?.enabled && twExt.totalItems != null) {
            stableTotal.value = twExt.totalItems;
            stableTotalPages.value = twExt.totalPages ?? Math.ceil(twExt.totalItems / (twExt.pageSize || limit.value));
        } else {
            stableTotal.value = getTotal.value;
            stableTotalPages.value = getTotalPagesCount.value;
        }
    }

    // On manufacturer PLPs Tweakwise may report 0 results while Shopware still
    // returns fallback products in `elements`. Treat those as no results so the
    // grid stays empty instead of showing phantom products.
    function tweakwiseReportsEmpty(): boolean {
        const twExt = (getCurrentListing.value as any)?.extensions?.tweakwise;
        return (keepFilters.value || !!manufacturer) && twExt?.enabled === true && twExt?.totalItems === 0;
    }

    // Shopware's store-api strips ad-hoc entity extensions, so the per-product
    // Tweakwise attributes arrive as a listing-level map keyed by product id.
    // Stamp them onto the products for consumers of
    // product.extensions.tweakwiseAttributes. (The "new" flag is served as the
    // product's native isNew field, not through this map.)
    function withTweakwiseAttributes(products: any[], listing?: any): any[] {
        const map = (listing ?? (getCurrentListing.value as any))?.extensions?.tweakwiseItemAttributes;
        if (!map) return products;
        for (const product of products) {
            const attrs = map[product?.id];
            if (attrs) {
                product.extensions = { ...(product.extensions || {}), tweakwiseAttributes: attrs };
            }
        }
        return products;
    }

    function accumulateProducts(page: number) {
        withTweakwiseAttributes(getElements.value);
        if (isLoadingMore.value) {
            const existingIds = new Set(allProducts.value.map((p: any) => p.id));
            const newProducts = getElements.value.filter((p: any) => !existingIds.has(p.id));
            allProducts.value = [...allProducts.value, ...newProducts];
            endPage.value = page;
            isLoadingMore.value = false;
        } else if (isLoadingPrevious.value) {
            const existingIds = new Set(allProducts.value.map((p: any) => p.id));
            const newProducts = getElements.value.filter((p: any) => !existingIds.has(p.id));
            const heightBefore = productsContainer.value?.scrollHeight || 0;

            allProducts.value = [...newProducts, ...allProducts.value];
            startPage.value = page;
            isLoadingPrevious.value = false;

            nextTick(() => {
                const heightAfter = productsContainer.value?.scrollHeight || 0;
                const heightDiff = heightAfter - heightBefore;
                if (heightDiff > 0) window.scrollBy(0, heightDiff);
            });

            if (endPage.value !== page) {
                isRestoringPage.value = true;
                router.replace({ query: { ...route.query, p: endPage.value.toString() } });
            }
        } else {
            allProducts.value = tweakwiseReportsEmpty() ? [] : [...getElements.value];
            startPage.value = page;
            endPage.value = page;
        }
    }

    async function loadMoreProducts(): Promise<void> {
        if (endPage.value >= stableTotalPages.value) return;
        isLoadingMore.value = true;
        await router.push({
            query: { ...route.query, p: (endPage.value + 1).toString() },
        });
    }

    async function loadPreviousProducts(): Promise<void> {
        if (startPage.value <= 1) return;
        isLoadingPrevious.value = true;

        const pagesToLoad: number[] = [];
        for (let p = 1; p < startPage.value; p++) {
            pagesToLoad.push(p);
        }

        const allNewProducts: any[] = [];
        const existingIds = new Set(allProducts.value.map((p: any) => p.id));
        const minPrice = queryStr(route.query['min-price']);
        const maxPrice = queryStr(route.query['max-price']);

        for (const p of pagesToLoad) {
            const searchOptions: any = {
                filter: [],
                properties: route.query.properties || undefined,
                limit: limit.value,
                p,
                'min-price': minPrice ? parseInt(minPrice, 10) : undefined,
                'max-price': maxPrice ? parseInt(maxPrice, 10) : undefined,
                order: effectiveCurrentSort.value || undefined,
                search: searchTerm.value || undefined,
                categoryPath: queryStr(route.query.tn_cid) || brandCategoryId.value || undefined,
                includeSubcategories: true,
                newOnly: queryStr(route.query.newOnly) || undefined,
            };

            try {
                await search(searchOptions);
            } catch {
                break;
            }

            withTweakwiseAttributes(getElements.value);
            for (const product of getElements.value) {
                if (!existingIds.has(product.id)) {
                    allNewProducts.push(product);
                    existingIds.add(product.id);
                }
            }
        }

        allProducts.value = [...allNewProducts, ...allProducts.value];
        startPage.value = 1;
        isLoadingPrevious.value = false;

        nextTick(() => {
            productsContainer.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    const initialSearchPromise = ref<Promise<void> | null>(null);
    let initialSearchResolve: (() => void) | null = null;
    if (import.meta.server) {
        initialSearchPromise.value = new Promise<void>(resolve => {
            initialSearchResolve = resolve;
        });
    }

    watch(
        () => route.query,
        (newQuery, oldQuery) => {
            if (isRestoringFromCache.value && !oldQuery) {
                isRestoringFromCache.value = false;
                initialSearchResolve?.();
                initialSearchResolve = null;
                return;
            }
            if (isRestoringPage.value) {
                isRestoringPage.value = false;
                return;
            }

            const properties = newQuery.properties || undefined;
            const minPrice = queryStr(newQuery['min-price']);
            const maxPrice = queryStr(newQuery['max-price']);
            const sort = queryStr(newQuery.sort);
            const keyword = queryStr(newQuery.search);
            const tnQuery = queryStr(newQuery.tn_q);
            const tnCid = queryStr(newQuery.tn_cid);
            let page = parseInt(queryStr(newQuery.p), 10) || 1;

            let filtersChanged = false;
            if (!isLoadingMore.value && !isLoadingPrevious.value && oldQuery) {
                filtersChanged = haveListingFiltersChanged(newQuery, oldQuery);

                if (filtersChanged) {
                    allProducts.value = [];

                    if (page !== 1) {
                        page = 1;
                        router.push({ query: { ...newQuery, p: '1' } });
                        return;
                    }
                }
            }

            const gen = ++searchGeneration;

            if (searchDebounceTimer) {
                clearTimeout(searchDebounceTimer);
                searchDebounceTimer = null;
            }

            const effectiveSort = sort || sortBy || undefined;
            const effectiveSearchTerm = tnQuery || keyword || manufacturer || undefined;

            const searchOptions: any = {
                filter: [],
                properties,
                limit: limit.value,
                p: page,
                'min-price': minPrice ? parseInt(minPrice, 10) : undefined,
                'max-price': maxPrice ? parseInt(maxPrice, 10) : undefined,
                order: effectiveSort,
                search: effectiveSearchTerm,
                categoryPath: tnCid || brandCategoryId.value || undefined,
                includeSubcategories: true,
                newOnly: queryStr(newQuery.newOnly) || undefined,
            };

            if (keyword && !tnQuery) {
                searchOptions.filter.push({
                    type: 'multi',
                    operator: 'OR',
                    queries: [
                        { type: 'contains', field: 'name', value: keyword },
                        { type: 'contains', field: 'description', value: keyword },
                        { type: 'contains', field: 'manufacturerNumber', value: keyword },
                        { type: 'contains', field: 'ean', value: keyword },
                        { type: 'contains', field: 'productNumber', value: keyword },
                        { type: 'contains', field: 'manufacturer.name', value: keyword },
                    ],
                });
            }

            const executeSearch = async () => {
                try {
                    await search(searchOptions);
                } catch {
                    isLoadingMore.value = false;
                    isLoadingPrevious.value = false;
                    return;
                }

                if (gen !== searchGeneration) return;

                updateStableState();
                accumulateProducts(page);

                // Snapshot the settled state into the payload for the client's first render.
                // Runs before Nuxt serializes, because SSR awaits `initialSearchPromise`.
                if (import.meta.server) {
                    ssrHandoff.value = {
                        allProducts: allProducts.value.map(slimProductForPayload),
                        startPage: startPage.value,
                        endPage: endPage.value,
                        stableTotal: stableTotal.value,
                        stableTotalPages: stableTotalPages.value,
                        stableFilters: stableFilters.value,
                        stableCurrentFilters: stableCurrentFilters.value,
                        stableSorts: stableSorts.value,
                        lastSettledFullPath: lastSettledFullPath.value,
                    };
                }

                onSearchComplete?.(tweakwiseReportsEmpty() ? [] : getElements.value);
            };

            if (!oldQuery || isLoadingMore.value || isLoadingPrevious.value) {
                const run = executeSearch();
                if (!oldQuery && initialSearchResolve) {
                    run.finally(() => {
                        initialSearchResolve?.();
                        initialSearchResolve = null;
                    });
                }
            } else {
                searchDebounceTimer = setTimeout(executeSearch, 150);
            }
        },
        { immediate: true },
    );

    onUnmounted(() => {
        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer);
            searchDebounceTimer = null;
        }
        if (allProducts.value.length > 0) {
            savePlpState({
                allProducts: allProducts.value,
                startPage: startPage.value,
                endPage: endPage.value,
                stableTotal: stableTotal.value,
                stableTotalPages: stableTotalPages.value,
                stableFilters: stableFilters.value,
                stableCurrentFilters: stableCurrentFilters.value,
                stableSorts: stableSorts.value,
            });
        }
    });

    return {
        allProducts,
        startPage,
        endPage,
        productsContainer,
        stableTotal,
        stableTotalPages,
        stableFilters,
        stableCurrentFilters,
        stableSorts,
        effectiveCurrentSort,
        isSearchPage,
        loading,
        search,
        getLimit,
        getElements,
        setCurrentFilters,
        getCurrentListing,
        loadMoreProducts,
        loadPreviousProducts,
        brandCategoryId,
        stableTweakwiseCategoryNames,
        stableTweakwiseSelectedSubtreeNames,
        stableSearchCategoryTree,
        stableManufacturerCategoryTree,
        lastSettledFullPath,
        hasSettledOnce,
        initialSearchPromise,
    };
}
